// components/FrenchNarrationInfo.tsx
// NOTE: Update frontend/src/components/__tests__/FrenchNarrationInfo.test.tsx and run
// `../scripts/test-frontend.sh FrenchNarrationInfo` when modifying this component.
import React from 'react'
import { Modal, Button, Typography } from 'antd'

export const FrenchNarrationTooltipContent = () => (
  <div style={{ maxWidth: 3600 }}>
    <div><strong>AI Detect</strong>: Recommends a French narration style from source tense, voice, POV, register, genre, and subgenre. You can override it.</div>
    <div><strong>Modern/Accessible Past Tense</strong>: Passé composé for current, close, conversational, romance-first, memoir-like, or commercial contemporary narration.</div>
    <div><strong>Classical/Literary Past Tense</strong>: Passé simple for formal, literary, historical, epic, retrospective, plot-first, or traditional adventure narration.</div>
    <div><strong>Present Tense</strong>: Présent de narration when the source narration is mainly present tense. It does not convert POV.</div>
    <div><strong>Mixed</strong>: Reserved for deliberate past/present frame structures, not ordinary flashbacks or dialogue.</div>
    <div style={{ marginTop: 6 }}>Need more guidance? Open "Learn more" for genre and subgenre cues.</div>
  </div>
)

interface FrenchNarrationInfoProps {
  linkClassName?: string
  linkStyle?: React.CSSProperties
  variant?: 'full' | 'simple'
}

const FrenchNarrationInfo: React.FC<FrenchNarrationInfoProps> = ({
  linkClassName,
  linkStyle,
  variant = 'full',
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const modalContent = React.useMemo(() => {
    if (variant === 'simple') {
      return (
        <>
          <Typography.Paragraph>
            Pick the tone that matches your book’s source narration. AI Detect is a recommendation, not a guarantee.
          </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>Modern/Accessible</Typography.Text>: Passé composé for current, close, conversational, confessional, romance-first, memoir-like, or commercial contemporary narration. Contemporary romance, rom-coms, cozy genres, YA/NA, urban fantasy, LitRPG, and fast modern thrillers often fit here when the prose is informal or immediate.
          </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>Classical/Literary</Typography.Text>: Passé simple for formal, literary, historical, epic, retrospective, plot-first, or traditional adventure narration. Historical fiction, literary drama, gothic, epic fantasy, serious SF, family sagas, and period settings often fit here when the prose has distance or a written cadence.
          </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>Present Tense</Typography.Text>: Uses présent de narration when the source book is mainly present tense. This preserves source tense and POV; it is not a POV conversion feature.
          </Typography.Paragraph>
          <Typography.Paragraph>
            Prose features override genre. A literary contemporary romance may still fit Classical/Literary, and a conversational SF or fantasy voice may still fit Modern/Accessible.
          </Typography.Paragraph>
        </>
      )
    }
    return (
      <>
        <Typography.Paragraph>
          Pick the tone that matches your book's source narration. AI Detect recommends a style from source tense, POV, prose register, narrative distance, pacing, genre, and subgenre. Authors should override it when they know the intended voice or market fit better.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Modern/Accessible Past Tense</Typography.Text>: Uses passé composé for completed past actions, with imparfait for background/state narration and plus-que-parfait for anteriority. Best for current, conversational, immediate, close, diary-like, memoir-like, confessional, romance-first, or commercial contemporary narration.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Modern/Accessible Past Tense & Present Tense</Typography.Text>: Keeps passé composé for past-tense layers and uses présent de narration only where the source deliberately moves into present-tense narration. Use for true alternating frame structures, not ordinary flashbacks.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Classical/Literary Past Tense</Typography.Text>: Uses passé simple for foregrounded completed narrative events, with imparfait for background/description and plus-que-parfait for anteriority. Best for traditional written French narration, formal or literary prose, historical or epic tone, structured adventure, plot-driven speculative fiction, period settings, and retrospective narration.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Classical/Literary Past Tense & Present Tense</Typography.Text>: Keeps passé simple for retrospective or past-tense layers and uses présent de narration only where the source deliberately alternates into present narration, such as dual timelines, journals, case files, or frame chapters.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Present Tense</Typography.Text>: Uses présent de narration when the source book is mainly present tense. This preserves source present-tense narration and does not convert third-person past into first-person present.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Genre is a tie-breaker, not a hard rule. Contemporary romance, rom-coms, cozy genres, YA/NA, memoir-like fiction, and conversational commercial voices often lean Modern/Accessible. Historical fiction, literary drama, gothic, epic fantasy, serious SF, sagas, and traditional adventure often lean Classical/Literary when the prose is formal, retrospective, or plot-first.
        </Typography.Paragraph>
      </>
    )
  }, [variant])

  return (
    <>
      <Button
        type="link"
        size="small"
        onClick={() => setIsOpen(true)}
        style={{ padding: 0, marginLeft: 12, ...linkStyle }}
        className={linkClassName}
      >
        Learn more
      </Button>
      <Modal
        title="French Narrative Styles"
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
        width={560}
      >
        {modalContent}
      </Modal>
    </>
  )
}

export default FrenchNarrationInfo
